import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import type { Id, RoleCode, StaffRole, StaffUser } from "../types";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import UserPermissionsEditor from "./UserPermissionsEditor";

const ROLE_LABELS: Partial<Record<RoleCode, string>> = {
  admin: "Administración",
  director: "Dirección",
  sales_manager: "Jefa / responsable de ventas",
  sales: "Vendedora",
  reception: "Recepción",
  professional: "Profesional",
  marketing: "Marketing",
};

function roleLabel(user: StaffUser) {
  return user.role_name || ROLE_LABELS[user.role_code] || user.role_code;
}

function toLocalDateTime(value?: string | null) {
  if (!value) return "";

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) return "";

  const local = new Date(
    date.getTime() -
      date.getTimezoneOffset() * 60_000
  );

  return local.toISOString().slice(0, 16);
}

function toIsoDateTime(value: string) {
  return value
    ? new Date(value).toISOString()
    : null;
}

export default function UsersConfig() {
  const {
    user: currentUser,
    locations,
    hasPermission,
  } = useAuth();
  const { show } = useToast();

  const [users, setUsers] = useState<StaffUser[]>([]);
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<StaffUser | null>(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleCode, setRoleCode] = useState<RoleCode>("sales");
  const [password, setPassword] = useState("");
  const [allLocations, setAllLocations] = useState(true);
  const [locationIds, setLocationIds] = useState<Id[]>([]);
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [passwordUser, setPasswordUser] = useState<StaffUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [permissionUser, setPermissionUser] = useState<StaffUser | null>(null);

  const canManage = hasPermission("users.manage");
  const canManagePermissions = hasPermission("permissions.manage");

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return a.full_name.localeCompare(b.full_name, "es");
      }),
    [users]
  );

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [userRows, roleRows] = await Promise.all([
        api.users(),
        canManage ? api.userRoles() : Promise.resolve([]),
      ]);

      setUsers(userRows);
      setRoles(roleRows);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar los usuarios.", "error");
    } finally {
      setLoading(false);
    }
  }, [canManage, show]);

  useEffect(() => {
    load();
  }, [load]);

  function clearForm() {
    setEditing(null);
    setFullName("");
    setEmail("");
    setPhone("");
    setRoleCode("sales");
    setPassword("");
    setAllLocations(true);
    setLocationIds([]);
    setValidFrom("");
    setValidUntil("");
  }

  function openNew() {
    clearForm();
    setShowForm(true);
  }

  function openEdit(item: StaffUser) {
    setEditing(item);
    setFullName(item.full_name);
    setEmail(item.email || "");
    setPhone(item.phone || "");
    setRoleCode(item.role_code);
    setPassword("");
    setAllLocations(
      item.all_locations !== false
    );
    setLocationIds(
      item.location_ids || []
    );
    setValidFrom(
      toLocalDateTime(
        item.valid_from
      )
    );
    setValidUntil(
      toLocalDateTime(
        item.valid_until
      )
    );
    setShowForm(true);
  }

  function closeForm() {
    if (submitting) return;
    setShowForm(false);
    clearForm();
  }

  async function submit(e: any) {
    e.preventDefault();

    if (!fullName.trim() || !email.trim() || !roleCode) {
      show("Completá nombre, email y rol.", "error");
      return;
    }

    if (!editing && password.length < 14) {
      show("La contraseña inicial debe tener al menos 14 caracteres.", "error");
      return;
    }

    if (
      editing?.membership_id &&
      !allLocations &&
      locationIds.length === 0
    ) {
      show(
        "Seleccioná al menos una sede o habilitá todas las sedes.",
        "error"
      );
      return;
    }

    if (
      editing?.membership_id &&
      validFrom &&
      validUntil &&
      new Date(validUntil).getTime() <=
        new Date(validFrom).getTime()
    ) {
      show(
        "El vencimiento debe ser posterior al inicio de la vigencia.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    try {
      if (editing) {
        if (editing.membership_id) {
          await api.updateUser(
            editing.id,
            {
              role_code:
                roleCode,
              all_locations:
                allLocations,
              location_ids:
                allLocations
                  ? []
                  : locationIds,
              ...(validFrom
                ? {
                    valid_from:
                      toIsoDateTime(
                        validFrom
                      ) as string,
                  }
                : {}),
              valid_until:
                toIsoDateTime(
                  validUntil
                ),
            }
          );
        } else {
          await api.updateUser(
            editing.id,
            {
              full_name:
                fullName.trim(),
              email:
                email
                  .trim()
                  .toLowerCase(),
              phone:
                phone.trim() ||
                null,
              role_code:
                roleCode,
            }
          );
        }

        show("Usuario actualizado.", "success");
      } else {
        await api.createUser({
          full_name: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          role_code: roleCode,
          password,
        });

        show("Usuario creado.", "success");
      }

      setShowForm(false);
      clearForm();
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo guardar el usuario.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(item: StaffUser) {
    const isSelf = String(currentUser?.id) === String(item.id);

    if (isSelf && item.active) {
      show("No podés desactivar tu propio usuario.", "error");
      return;
    }

    try {
      await api.updateUser(item.id, {
        active: !item.active,
      });

      show(
        item.active ? "Usuario desactivado." : "Usuario activado.",
        "success"
      );

      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo actualizar el estado del usuario.", "error");
    }
  }

  function openResetPassword(item: StaffUser) {
    setPasswordUser(item);
    setNewPassword("");
  }

  function closeResetPassword() {
    if (resettingPassword) return;
    setPasswordUser(null);
    setNewPassword("");
  }

  async function resetPassword(e: any) {
    e.preventDefault();

    if (!passwordUser) return;

    if (newPassword.length < 14) {
      show("La nueva contraseña debe tener al menos 14 caracteres.", "error");
      return;
    }

    setResettingPassword(true);

    try {
      await api.resetUserPassword(passwordUser.id, newPassword);

      show(
        `Contraseña de ${passwordUser.full_name} actualizada.`,
        "success"
      );

      setPasswordUser(null);
      setNewPassword("");
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo resetear la contraseña.", "error");
    } finally {
      setResettingPassword(false);
    }
  }

  return (
    <>
      <div className="kanban-header">
        <div>
          <h2>Usuarios y permisos</h2>
          <p className="meta">
            Administrá accesos, roles y estado de las personas que trabajan en Mauvelore.
            Los usuarios con historial nunca se eliminan: se desactivan.
            {canManagePermissions
              ? " También podés configurar permisos individuales."
              : ""}
          </p>
        </div>

        {canManage && (
          <button className="btn-primary" onClick={openNew}>
            + Nuevo usuario
          </button>
        )}
      </div>

      {!canManage && (
        <div className="card">
          <p className="meta">
            Tu usuario puede consultar el equipo, pero sólo Administración o Dirección
            pueden modificar accesos y roles.
          </p>
        </div>
      )}

      {loading && <div className="loading-strip">Cargando usuarios...</div>}

      <div className="contact-list">
        {sortedUsers.map((item) => {
          const isSelf = String(currentUser?.id) === String(item.id);

          return (
            <div key={item.id} className="contact-row">
              <div
                style={{
                  flex: 1,
                  cursor: canManage ? "pointer" : "default",
                  minWidth: 0,
                }}
                onClick={() => canManage && openEdit(item)}
              >
                <div className="name">
                  {item.full_name}
                  {isSelf && " · Vos"}
                  {!item.active && " · Inactivo"}
                </div>

                <div className="meta">
                  {item.email}
                  {item.phone ? ` · ${item.phone}` : ""}
                </div>

                <div className="meta" style={{ marginTop: 4 }}>
                  Rol: {roleLabel(item)}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <span className={`pill ${item.active ? "pill-ok" : "pill-bad"}`}>
                  {item.active ? "Activo" : "Inactivo"}
                </span>

                {canManage && (
                  <>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => openEdit(item)}
                    >
                      Editar
                    </button>

                    {canManagePermissions && (
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setPermissionUser(item)}
                      >
                        Permisos
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => openResetPassword(item)}
                    >
                      Reset contraseña
                    </button>

                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={isSelf && item.active}
                      title={
                        isSelf && item.active
                          ? "No podés desactivar tu propio usuario."
                          : undefined
                      }
                      onClick={() => toggleActive(item)}
                    >
                      {item.active ? "Desactivar" : "Activar"}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {!loading && sortedUsers.length === 0 && (
          <p className="meta">
            No hay usuarios configurados.
          </p>
        )}
      </div>

      {showForm && (
        <div className="modal-backdrop" onClick={closeForm}>
          <div
            className="modal modal-wide"
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>{editing ? "Editar acceso" : "Nuevo usuario"}</h3>

            {editing?.membership_id && (
              <p className="meta">
                Nombre, email y teléfono pertenecen a la identidad compartida
                de la persona. En esta pantalla se modifica únicamente su
                acceso a la organización actual.
              </p>
            )}

            <form onSubmit={submit} className="form-grid">
              <label>Nombre completo</label>
              <input
                value={fullName}
                onChange={(e: any) => setFullName(e.target.value)}
                placeholder="Ej: Florencia Pérez"
                required
                disabled={Boolean(editing?.membership_id)}
              />

              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                placeholder="nombre@clinicmauvelore.com"
                required
                disabled={Boolean(editing?.membership_id)}
              />

              <label>Teléfono</label>
              <input
                value={phone}
                onChange={(e: any) => setPhone(e.target.value)}
                placeholder="+54 9 ..."
                disabled={Boolean(editing?.membership_id)}
              />

              <label>Rol</label>
              <select
                value={roleCode}
                onChange={(e: any) =>
                  setRoleCode(e.target.value as RoleCode)
                }
                required
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.code}>
                    {role.name}
                  </option>
                ))}
              </select>

              {editing?.membership_id && (
                <>
                  <label>Alcance de sedes</label>
                  <div>
                    <label
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        fontWeight: 500,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allLocations}
                        onChange={(e: any) => {
                          setAllLocations(
                            e.target.checked
                          );

                          if (e.target.checked) {
                            setLocationIds([]);
                          }
                        }}
                      />
                      Todas las sedes
                    </label>

                    {!allLocations && (
                      <div
                        style={{
                          display: "grid",
                          gap: 8,
                          marginTop: 10,
                        }}
                      >
                        {locations.map((location) => {
                          const checked =
                            locationIds.some(
                              (id) =>
                                String(id) ===
                                String(location.id)
                            );

                          return (
                            <label
                              key={location.id}
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                fontWeight: 500,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e: any) => {
                                  if (e.target.checked) {
                                    setLocationIds(
                                      (current) => [
                                        ...current,
                                        location.id,
                                      ]
                                    );
                                  } else {
                                    setLocationIds(
                                      (current) =>
                                        current.filter(
                                          (id) =>
                                            String(id) !==
                                            String(location.id)
                                        )
                                    );
                                  }
                                }}
                              />
                              {location.name}
                            </label>
                          );
                        })}

                        {locations.length === 0 && (
                          <p className="meta">
                            No hay sedes activas disponibles.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <label>Vigente desde</label>
                  <input
                    type="datetime-local"
                    value={validFrom}
                    onChange={(e: any) =>
                      setValidFrom(e.target.value)
                    }
                  />

                  <label>Vigente hasta</label>
                  <div>
                    <input
                      type="datetime-local"
                      value={validUntil}
                      onChange={(e: any) =>
                        setValidUntil(e.target.value)
                      }
                    />
                    <p
                      className="meta"
                      style={{ marginTop: 6 }}
                    >
                      Dejalo vacío para que el acceso no tenga vencimiento.
                    </p>
                  </div>
                </>
              )}

              {!editing && (
                <>
                  <label>Contraseña inicial</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    placeholder="Mínimo 14 caracteres"
                    minLength={14}
                    required
                    autoComplete="new-password"
                  />

                  <span />
                  <p className="meta">
                    El usuario podrá ingresar inmediatamente con esta contraseña.
                    Más adelante incorporaremos cambio obligatorio en el primer acceso.
                  </p>
                </>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || !roleCode}
                >
                  {submitting ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {permissionUser && (
        <UserPermissionsEditor
          user={permissionUser}
          onClose={() => setPermissionUser(null)}
        />
      )}

      {passwordUser && (
        <div className="modal-backdrop" onClick={closeResetPassword}>
          <div
            className="modal"
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>Resetear contraseña</h3>

            <p className="meta">
              Usuario: <b>{passwordUser.full_name}</b>
              <br />
              {passwordUser.email}
            </p>

            <form onSubmit={resetPassword} className="form-grid">
              <label>Nueva contraseña</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
                placeholder="Mínimo 14 caracteres"
                minLength={14}
                required
                autoComplete="new-password"
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeResetPassword}
                  disabled={resettingPassword}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={resettingPassword}
                >
                  {resettingPassword
                    ? "Actualizando..."
                    : "Actualizar contraseña"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
